/**
 * Contract Ledger Update — Frontend ローカル Contract
 *
 * oz.file() を使用して OpenAPILink が File 検出時に
 * multipart/form-data（Bracket Notation）を自動送信する。
 * sections / attributes は契約詳細 GET と同様のメタデータ・構造化 value（ADDRESS 等）を許容する（未知キーは catchall）。
 *
 * @standandforce/api-contract は Zod v3 ベースのため z.file() が存在せず、
 * ファイルアップロードを伴う Contract は共有パッケージで定義できない。
 * Frontend は @orpc/zod の oz.file() で代替し、ローカルに Contract を定義している。
 * @standandforce/api-contract が Zod v4 に移行すれば共有 Contract に統合可能。
 */
import { oc } from '@orpc/contract';
import { oz } from '@orpc/zod';
import { z } from 'zod';

const UploadedFileInfoSchema = z.object({
  s3Url: z.string(),
  originalname: z.string().optional(),
  mimetype: z.string().optional(),
  size: z.number().optional(),
  fieldname: z.string().optional(),
});

/** ADDRESS 等、OpenAPI / 契約詳細と同型の構造化 value */
const StructuredAttributeValueSchema = z.record(z.string(), z.unknown());

const AttributeValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  oz.file(),
  UploadedFileInfoSchema,
  z.array(z.union([z.string(), z.number(), oz.file(), UploadedFileInfoSchema])),
  z.null(),
  StructuredAttributeValueSchema,
]);

const ContractAttributeSchema = z
  .object({
    attributeKey: z.string(),
    value: AttributeValueSchema.optional(),
  })
  .catchall(z.unknown());

const ContractSectionSchema = z
  .object({
    sectionKey: z.string(),
    attributes: z.array(ContractAttributeSchema).default([]),
  })
  .catchall(z.unknown());

export const contractLedgerContract = oc.router({
  updateContract: oc
    .route({
      method: 'PUT',
      path: '/v2/contract-ledger/contracts/{contractId}',
    })
    .input(
      z.object({
        contractId: z.string(),
        sections: z.array(ContractSectionSchema),
      }),
    )
    .output(
      z.object({
        contractId: z.string(),
      }),
    ),
});
